<template>
  <div class="mobile-tab-fold" :class="{ 'has-more': hiddenTabs.length }" ref="root">
    <div class="tab-row">
      <div
        class="tab-item"
        :class="{ active: tab.name === value }"
        v-for="tab in visibleTabs"
        :key="'v-' + tab.name"
        @click="select(tab)"
      >{{ tab.label }}</div>
      <div class="more-wrap" v-if="hiddenTabs.length">
        <button
          type="button"
          class="more-btn"
          :class="{ open }"
          aria-label="更多分类"
          @click.stop="open = !open"
        >
          <i class="el-icon-arrow-down"></i>
        </button>
        <div class="fold-list" v-show="open">
          <div
            class="fold-item"
            :class="{ active: tab.name === value }"
            v-for="tab in hiddenTabs"
            :key="'h-' + tab.name"
            @click="select(tab)"
          >{{ tab.label }}</div>
        </div>
      </div>
    </div>
    <div class="measure" ref="measure" aria-hidden="true">
      <span
        class="measure-item"
        v-for="tab in tabs"
        :key="'m-' + tab.name"
        :data-name="tab.name"
      >{{ tab.label }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MobileTabFold',
  props: {
    tabs: {
      type: Array,
      default: () => []
    },
    value: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      open: false,
      visibleStart: 0,
      visibleCount: 1
    }
  },
  computed: {
    visibleTabs() {
      const list = this.tabs || []
      if (!list.length) return []
      const start = Math.min(Math.max(this.visibleStart, 0), list.length - 1)
      const count = Math.min(Math.max(this.visibleCount, 1), list.length - start)
      return list.slice(start, start + count)
    },
    hiddenTabs() {
      const shown = this.visibleTabs.map(tab => tab.name)
      return (this.tabs || []).filter(tab => !shown.includes(tab.name))
    }
  },
  watch: {
    tabs: {
      handler() {
        this.scheduleMeasure()
      },
      deep: true
    },
    value() {
      this.open = false
      this.scheduleMeasure()
    }
  },
  mounted() {
    this.scheduleMeasure()
    window.addEventListener('resize', this.scheduleMeasure)
    document.addEventListener('click', this.onDocClick)
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(this.scheduleMeasure)
      this._ro.observe(this.$el)
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.scheduleMeasure)
    document.removeEventListener('click', this.onDocClick)
    if (this._ro) this._ro.disconnect()
  },
  methods: {
    onDocClick(e) {
      if (this.open && this.$el && !this.$el.contains(e.target)) {
        this.open = false
      }
    },
    select(tab) {
      this.open = false
      if (tab.name !== this.value) {
        this.$emit('input', tab.name)
      }
    },
    scheduleMeasure() {
      this.$nextTick(() => {
        this.updateVisibleCount()
        requestAnimationFrame(this.updateVisibleCount)
      })
    },
    updateVisibleCount() {
      const root = this.$refs.root
      const measure = this.$refs.measure
      const list = this.tabs || []
      if (!root || !measure || !list.length) return
      if (root.clientWidth < 40) return
      const moreW = 44
      const gap = 8
      const styles = window.getComputedStyle(root)
      const rowW = root.clientWidth
        - parseFloat(styles.paddingLeft || 0)
        - parseFloat(styles.paddingRight || 0)
      const widths = list.map((tab) => {
        const el = Array.from(measure.children).find(
          node => node.getAttribute('data-name') === tab.name
        )
        return (el && el.offsetWidth) || 80
      })
      const rangeWidth = (start, end) => {
        let used = 0
        for (let i = start; i <= end; i += 1) {
          used += widths[i] + (i > start ? gap : 0)
        }
        return used
      }
      const last = list.length - 1
      if (rangeWidth(0, last) <= rowW) {
        this.visibleStart = 0
        this.visibleCount = list.length
        return
      }
      const avail = Math.max(rowW - moreW, widths[0] || 80)
      let activeIdx = list.findIndex(tab => tab.name === this.value)
      if (activeIdx < 0) activeIdx = 0
      let start = activeIdx
      let end = activeIdx
      let used = widths[activeIdx]
      while (start > 0) {
        const next = used + gap + widths[start - 1]
        if (next > avail) break
        used = next
        start -= 1
      }
      while (end < last) {
        const next = used + gap + widths[end + 1]
        if (next > avail) break
        used = next
        end += 1
      }
      this.visibleStart = start
      this.visibleCount = Math.max(end - start + 1, 1)
    }
  }
}
</script>

<style lang="scss" scoped>
.mobile-tab-fold {
  display: none;
  width: 100%;
  margin: 0 0 1.6rem;
  padding: 0 1.6rem;
  box-sizing: border-box;
  position: relative;
  z-index: 6;
  flex: 0 0 auto;
}

.measure {
  position: absolute;
  left: 0;
  top: 0;
  visibility: hidden;
  pointer-events: none;
  white-space: nowrap;
  height: 0;
  overflow: hidden;
}

.measure-item {
  display: inline-block;
  padding: 1.1rem 1.2rem 1.2rem;
  font-size: 1.4rem;
  font-weight: 500;
  line-height: 2rem;
  white-space: nowrap;
}

@media (max-width: 767px) {
  .mobile-tab-fold {
    display: block;
    width: 100%;
  }

  .tab-row {
    display: flex;
    align-items: center;
    min-height: 4.4rem;
    border-bottom: 0.1rem solid #e8eef5;
  }

  .tab-item {
    display: inline-flex;
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
    padding: 1.1rem 1.2rem 1.2rem;
    font-size: 1.4rem;
    font-weight: 500;
    color: #666;
    line-height: 2rem;
    white-space: normal;
    word-break: break-word;
    overflow: visible;
    cursor: pointer;

    &.active {
      color: #FB0000;
      font-weight: 600;
      box-shadow: inset 0 -0.2rem 0 #FB0000;
    }
  }

  .has-more .tab-item {
    max-width: calc(100% - 4.4rem);
  }

  .more-wrap {
    position: relative;
    margin-left: auto;
    flex: 0 0 4.4rem;
  }

  .more-btn {
    width: 4.4rem;
    height: 4.4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;

    i {
      font-size: 1.6rem;
      color: #333;
      transition: transform .2s;
    }
    &.open i {
      transform: rotate(180deg);
    }
  }

  .fold-list {
    position: absolute;
    top: 100%;
    right: 0;
    min-width: 18rem;
    max-width: 28rem;
    background: #fff;
    border: 0.1rem solid #d8e0ea;
    border-radius: 0.8rem;
    box-shadow: 0 0.8rem 2.4rem rgba(0, 0, 0, .08);
    max-height: 28rem;
    overflow-y: auto;
  }

  .fold-item {
    padding: 1.2rem 1.6rem;
    font-size: 1.4rem;
    color: #333;
    line-height: 2.2rem;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
    &.active {
      color: #FB0000;
      font-weight: 600;
    }
  }
}
</style>
